/**
 * Cross-cutting users API (managers, coordinators, organizers, invitations, criminal record).
 * Volunteer / admin list+CRUD live in volunteers.service / admins.service.
 */

import apiClient from './api-client';
import type {
  UserWithProfile,
  UpdateUserProfileDto,
  CriminalRecordDto,
  CriminalRecordDecisionAction,
  GetUsersParams,
  InviteProspectDto,
  CriminalRecordUploadInfo,
  Learning360TutorPathStats,
  WithPhoneSetupLink,
} from '@/types/user.types';
import type { PaginatedResponse } from '@/types/data-table.types';

/**
 * `POST /users/:userId/regenerate-invitation` — resend the 14-day activation email.
 */
export const regenerateInvitation = async (userId: string): Promise<{ sent: boolean }> => {
  const { data } = await apiClient.post(
    `/users/${encodeURIComponent(userId)}/regenerate-invitation`,
  );
  return data;
};

/** `POST /users/:userId/send-password-reset` — send a password-reset email. */
export const sendPasswordReset = async (userId: string): Promise<{ sent: boolean }> => {
  const { data } = await apiClient.post(`/users/${encodeURIComponent(userId)}/send-password-reset`);
  return data;
};

/**
 * Appends availability filter params to a URLSearchParams.
 * When dayOfWeek + start + end are all set, sends precise slot filter.
 * Falls back to day-only filter (availabilityDays) otherwise.
 */
function appendAvailabilityParams(qp: URLSearchParams, params: GetUsersParams | undefined): void {
  if (
    params?.availabilityDayOfWeek !== undefined &&
    params?.availabilityStartTime &&
    params?.availabilityEndTime
  ) {
    qp.append('availabilityDayOfWeek', String(params.availabilityDayOfWeek));
    qp.append('availabilityStartTime', params.availabilityStartTime);
    qp.append('availabilityEndTime', params.availabilityEndTime);
  } else {
    params?.availabilityDays?.forEach((d) => qp.append('availabilityDays', String(d)));
  }
}

/** `POST /users/invite?companyId=` — invite an onboarding prospect. */
export const createProspect = async (
  prospectData: InviteProspectDto,
  companyId: string,
): Promise<{ user: unknown; invitationLink: string }> => {
  const { data } = await apiClient.post<{ user: unknown; invitationLink: string }>(
    `/users/invite?companyId=${companyId}`,
    { ...prospectData, skipInvitationEmail: true },
  );
  return data;
};

/** `GET /users/:id` — get any user by ID regardless of role. */
export const getUser = async (id: string): Promise<UserWithProfile> => {
  const { data } = await apiClient.get<UserWithProfile>(`/users/${encodeURIComponent(id)}`);
  return data;
};

/** `PATCH /users/:id` — update a profile, including the staff-only internal comment. */
export const updateUserProfile = async (
  id: string,
  dto: UpdateUserProfileDto,
): Promise<WithPhoneSetupLink<UserWithProfile>> => {
  const { data } = await apiClient.patch<WithPhoneSetupLink<UserWithProfile>>(
    `/users/${encodeURIComponent(id)}`,
    dto,
  );
  return data;
};

/** `GET /users/managers` — paginated managers. */
export const getManagers = async (
  params?: GetUsersParams,
): Promise<PaginatedResponse<UserWithProfile>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.excludeIds?.length) queryParams.append('excludeIds', params.excludeIds.join(','));

  const { data } = await apiClient.get(`/users/managers?${queryParams.toString()}`);
  return data;
};

/** `GET /users/managers/:identifier` */
export const getManager = async (identifier: string): Promise<UserWithProfile> => {
  const { data } = await apiClient.get(`/users/managers/${encodeURIComponent(identifier)}`);
  return data;
};

/** `GET /users/coordinators` — paginated coordinators. */
export const getCoordinators = async (
  params?: GetUsersParams,
): Promise<PaginatedResponse<UserWithProfile>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.excludeIds?.length) queryParams.append('excludeIds', params.excludeIds.join(','));
  if (params?.managedById) queryParams.append('managedById', params.managedById);
  if (params?.programName) queryParams.append('programName', params.programName);
  if (params?.establishmentId) queryParams.append('establishmentId', params.establishmentId);
  if (params?.roleCodes?.length)
    params.roleCodes.forEach((c) => queryParams.append('roleCodes', c));

  const { data } = await apiClient.get(`/users/coordinators?${queryParams.toString()}`);
  return data;
};

/** `GET /users/coordinators/:identifier` */
export const getCoordinator = async (identifier: string): Promise<UserWithProfile> => {
  const { data } = await apiClient.get(`/users/coordinators/${encodeURIComponent(identifier)}`);
  return data;
};

/** Single paginated call returning tutors + volunteers + coordinators combined. */
/** `GET /users/organizers` — tutors + volunteers + coordinators. */
export const getOrganizers = async (
  params?: GetUsersParams,
): Promise<PaginatedResponse<UserWithProfile>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.excludeIds?.length) queryParams.append('excludeIds', params.excludeIds.join(','));
  params?.statuses?.forEach((s) => queryParams.append('statuses', s));
  params?.subjectIds?.forEach((id) => queryParams.append('subjectIds', id));
  params?.roleCodes?.forEach((code) => queryParams.append('roleCodes', code));
  if (params?.establishmentId) queryParams.append('establishmentId', params.establishmentId);
  appendAvailabilityParams(queryParams, params);
  params?.roleGroups?.forEach((g) => queryParams.append('roleGroups', g));

  const { data } = await apiClient.get(`/users/organizers?${queryParams.toString()}`);
  return data;
};

/** `GET /users/superadmins` — paginated superadmins. */
export const getSuperadmins = async (
  params?: GetUsersParams,
): Promise<PaginatedResponse<UserWithProfile>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.excludeIds?.length) queryParams.append('excludeIds', params.excludeIds.join(','));

  const { data } = await apiClient.get(`/users/superadmins?${queryParams.toString()}`);
  return data;
};

/** `GET /users/superadmins/:identifier` */
export const getSuperadmin = async (identifier: string): Promise<UserWithProfile> => {
  const { data } = await apiClient.get(`/users/superadmins/${encodeURIComponent(identifier)}`);
  return data;
};

/** `GET /users/for-sharing` — users that can receive a share. */
export const getUsersForSharing = async (
  params?: GetUsersParams,
): Promise<PaginatedResponse<UserWithProfile>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.excludeIds?.length) queryParams.append('excludeIds', params.excludeIds.join(','));

  params?.roleCodes?.forEach((code) => queryParams.append('roleCodes', code));

  const { data } = await apiClient.get(`/users/for-sharing?${queryParams.toString()}`);
  return data;
};

// ─────────────────────────────────────────────────────────────────────────────
// Criminal record (shared across tutor types)
// ─────────────────────────────────────────────────────────────────────────────

/** `GET /users/:userId/criminal-record` */
export const getCriminalRecord = async (
  userId: string,
  companyId: string,
): Promise<CriminalRecordDto | null> => {
  const response = await apiClient.get<{ criminalRecord: CriminalRecordDto | null }>(
    `/users/${userId}/criminal-record?companyId=${companyId}`,
  );
  return response.data?.criminalRecord ?? null;
};

/** `POST /users/:userId/criminal-record/validate` */
export const updateCriminalRecordDecision = async (
  userId: string,
  action: CriminalRecordDecisionAction,
): Promise<{ criminalRecord: CriminalRecordDto; tutorPlatformStatus: string }> => {
  const response = await apiClient.post<{
    criminalRecord: CriminalRecordDto;
    tutorPlatformStatus: string;
  }>(`/users/${userId}/criminal-record/validate`, { action });
  return response.data;
};

/** `POST /users/:userId/criminal-record` — multipart file upload. */
export const uploadCriminalRecord = async (
  userId: string,
  file: File,
): Promise<CriminalRecordUploadInfo> => {
  const body = new FormData();
  body.append('file', file);
  const { data } = await apiClient.post<{ criminalRecord: CriminalRecordUploadInfo }>(
    `/users/${userId}/criminal-record`,
    body,
  );
  return data.criminalRecord;
};

/** `GET /users/:userId/criminal-record/download-url` */
export const getCriminalRecordDownloadUrl = async (userId: string): Promise<string | null> => {
  const { data } = await apiClient.get<{ url?: string }>(
    `/users/${userId}/criminal-record/download-url`,
  );
  return data.url ?? null;
};

/** `GET /learning360/tutors/:userId/path-stats` */
export const getLearning360TutorPathStats = async (
  userId: string,
): Promise<Learning360TutorPathStats> => {
  const { data } = await apiClient.get<Learning360TutorPathStats>(
    `/learning360/tutors/${userId}/path-stats`,
  );
  return data;
};

/** `DELETE /users/:id` — delete a user (hard delete). */
export const deleteUser = async (id: string): Promise<void> => {
  await apiClient.delete(`/users/${id}`);
};

/** `GET /users/check-contact` — check if email or phone already exists. */
export const checkContactExists = async (
  value: string,
  type: 'email' | 'phone',
  excludeUserId?: string,
): Promise<{ exists: boolean }> => {
  const params: Record<string, string> = { value, type };
  if (excludeUserId) params.excludeUserId = excludeUserId;
  const { data } = await apiClient.get<{ exists: boolean }>(`/users/check-contact`, {
    params,
  });
  return data;
};
